declare global {
    interface Window {
        __ENV__?: {
            VITE_APP_ENV?: string;
            apiUrl?: string;
            identityUrl?: string;
            acombagoOrigin?: string;
            supervisorHostSuffix?: string;
        };
    }
}

// VITE_APP_ENV is injected at container startup via docker-entrypoint.sh into /config.js.
// Falls back to 'local' for the Vite dev server, where config.js is served from public/.
const appEnv = window.__ENV__?.VITE_APP_ENV ?? 'local';

export const isDevToolsEnabled = appEnv === 'local' || appEnv === 'dev';

// The company's custom reminder email is not released yet: staging and production neither show its editor
// nor apply a stored template, so the reminder stays the AI or fixed one there.
export const isCustomEmailTemplateEnabled = appEnv === 'local' || appEnv === 'dev';

// An unset VITE_* var arrives as an empty string rather than undefined, so resolution has to
// fall through on any falsy value — nullish coalescing would latch onto '' and skip the fallback.
const firstConfigured = (...values: (string | undefined)[]) => values.find(v => v) ?? '';

// Resolution order:
//   1. window.__ENV__.apiUrl — injected at container startup from the API_URL env var
//      (lets one built image target staging/prod with their own API host, cross-origin).
//   2. VITE_API_URL — baked at build time (docker-compose uses '/api', local .env uses localhost).
//   3. localhost fallback for the bare Vite dev server.
export const BASE_URL = firstConfigured(
    window.__ENV__?.apiUrl,
    import.meta.env.VITE_API_URL,
    'http://localhost:5074',
);

// Identity (OIDC) authority and allowed AcombaGo embed origins follow the same resolution as
// apiUrl: window.__ENV__ (injected at container startup from ECS env vars) wins, so one built
// image serves staging/prod with their own values. VITE_* is the build-time fallback the local
// Vite dev server / docker-compose still bake in.
export const IDENTITY_URL = firstConfigured(
    window.__ENV__?.identityUrl,
    import.meta.env.VITE_IDENTITY_URL,
);

export const ACOMBAGO_ORIGIN = firstConfigured(
    window.__ENV__?.acombagoOrigin,
    import.meta.env.VITE_ACOMBAGO_ORIGIN,
);

// True when THIS page load came through pme-factory-supervisor. Injected at container startup like the
// values above (SUPERVISOR_HOST_SUFFIX=factory.pme.acceo.com), so one built image can run either way.
//
// Supervised, the app must NOT drive its own OIDC handshake: it is served from an instance subdomain whose
// redirect_uri is not registered with Identity Server — and cannot be, since the supervisor exists precisely
// so that only its own URL needs to be. oidcService swaps in a façade that reads the supervisor's session.
//
// Matched against the hostname rather than read as a boolean because ONE container serves two audiences: a
// reviewer arrives on <slug>.<suffix> through the supervisor, while the E2E suite drives the same stack on
// its published localhost port. Only the first has /_factory/* in front of it; a boolean would put the
// second on the supervised path too, where that endpoint resolves to the SPA's own index.html.
//
// firstConfigured for the same reason as the values above: an empty suffix in config.js means "no
// supervisor", exactly as an absent key does.
const supervisorHostSuffix = firstConfigured(window.__ENV__?.supervisorHostSuffix);

export const IS_SUPERVISED =
    supervisorHostSuffix !== '' && window.location.hostname.endsWith(`.${supervisorHostSuffix}`);
